/**
 * DataGrid Hook
 * 
 * Custom hook to manage DataGrid column widths
 * 
 * This hook is responsible for:
 * 1. Managing column widths, including user-resized columns
 * 2. Handling column resize interactions
 * 3. Providing column width information used for horizontal scrolling
 * 
 * Important for horizontal scrolling:
 * - The getColumnWidth function returns the width for each column
 * - These widths are used to calculate the total content width
 * - When total content width exceeds container width, horizontal scrolling is enabled
 * - Column resizing affects the total width and therefore the scrolling behavior
 */

import * as React from 'react';
import { Column, DataGridProps } from './types';

// Default column width
const DEFAULT_COLUMN_WIDTH = 150;

interface ColumnWidthState {
  columnWidths: Record<string, number>;
  isResizing: boolean;
  resizingColumn: string | null;
  startX: number;
  startWidth: number;
}


export function useDataGrid(props: DataGridProps) {
  const { columns } = props;

  // Initialize column widths from props with proper defaults
  const initialColumnWidths = React.useMemo(() => {
    return columns.reduce<Record<string, number>>((acc, column) => {
      // Use explicit width if defined, otherwise use default
      acc[column.name] = column.width ?? DEFAULT_COLUMN_WIDTH;
      return acc;
    }, {});
  }, [columns]);

  // State for column widths and resize info
  const [state, setState] = React.useState<ColumnWidthState>({
    columnWidths: initialColumnWidths,
    isResizing: false,
    resizingColumn: null,
    startX: 0,
    startWidth: 0
  });

  // Store a ref to the current resize state for event handlers
  const resizeStateRef = React.useRef({
    isResizing: false,
    columnName: null as string | null,
    startX: 0,
    startWidth: 0
  });

  // Update the ref when state changes
  React.useEffect(() => {
    resizeStateRef.current = {
      isResizing: state.isResizing,
      columnName: state.resizingColumn,
      startX: state.startX,
      startWidth: state.startWidth
    };
  }, [state.isResizing, state.resizingColumn, state.startX, state.startWidth]);

  // Column resize handlers
  const handleColumnResizeStart = React.useCallback(
    (columnName: string, width: number, clientX: number) => {
      setState(prev => ({
        ...prev,
        isResizing: true,
        resizingColumn: columnName,
        startX: clientX,
        startWidth: width ?? prev.columnWidths[columnName] ?? DEFAULT_COLUMN_WIDTH
      }));
    },
    []
  );

  const handleColumnResizing = React.useCallback(
    (clientX: number) => {
      const { isResizing, columnName, startX, startWidth } = resizeStateRef.current;
      
      if (!isResizing || !columnName) return;

      const column = columns.find(c => c.name === columnName);
      if (!column) return;

      // Get min/max constraints with fallbacks
      const minWidth = column.minWidth ?? 40;
      const maxWidth = column.maxWidth ?? 2000;
      
      const deltaX = clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));

      setState(prev => {
        // Only update if width actually changed
        if (prev.columnWidths[columnName] === newWidth) {
          return prev;
        }

        return {
          ...prev,
          columnWidths: {
            ...prev.columnWidths,
            [columnName]: newWidth,
          }
        };
      });
    },
    [columns]
  );

  const handleColumnResizeEnd = React.useCallback(
    () => {
      setState(prev => ({
        ...prev,
        isResizing: false,
        resizingColumn: null,
        startX: 0,
        startWidth: 0
      }));
    },
    []
  );

  // Get column width with proper fallbacks
  const getColumnWidth = React.useCallback(
    (column: Column): number => {
      const width = state.columnWidths[column.name];
      if (typeof width === 'number') {
        return width;
      }
      return column.width ?? DEFAULT_COLUMN_WIDTH;
    },
    [state.columnWidths]
  );

  // Get the position for the resize indicator
  const getResizeIndicatorPosition = React.useCallback(
    (): number => {
      if (!state.resizingColumn) return 0;
      
      const columnElement = document.getElementById(state.resizingColumn);
      if (!columnElement) return 0;
      
      // Get the column's position relative to the viewport
      const columnRect = columnElement.getBoundingClientRect();
      
      // Get the DataGrid container
      const dataGridContainer = columnElement.closest('.data-grid') as HTMLElement;
      if (!dataGridContainer) return columnRect.right;
      
      // Calculate position relative to the DataGrid container
      const containerRect = dataGridContainer.getBoundingClientRect();
      
      // Calculate the position of the resize indicator
      return columnRect.right - containerRect.left;
    },
    [state.resizingColumn]
  );

  // Track mouse events for column resizing
  React.useEffect(() => {
    if (!state.isResizing) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      handleColumnResizing(e.clientX);
    };

    const handleMouseUp = () => {
      handleColumnResizeEnd();
    };

    // Add event listeners to document
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [state.isResizing, handleColumnResizing, handleColumnResizeEnd]);

  // Return only what's needed
  return {
    columnWidths: state.columnWidths,
    isResizing: state.isResizing,
    resizingColumn: state.resizingColumn,
    actions: {
      handleColumnResizeStart,
      handleColumnResizing,
      handleColumnResizeEnd,
      getColumnWidth,
      getResizeIndicatorPosition
    }
  };
} 